import React, { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useLang } from "@/context/LangContext";

/**
 * Referrals tab — computes the invite subtree map from a flat `tree` list
 * (rows: {user_id, referrer_user_id, name, email, referral_code, role, is_approved, is_blocked}).
 * Renders summary tiles + a searchable, indented tree.
 */
export default function ReferralAnalytics({ tree }) {
  const { t } = useLang();
  const [query, setQuery] = useState("");

  const { treeMap, roots } = useMemo(() => {
    const byParent = new Map();
    tree.forEach((u) => {
      const p = u.referrer_user_id || "ROOT";
      if (!byParent.has(p)) byParent.set(p, []);
      byParent.get(p).push(u);
    });
    return { treeMap: byParent, roots: byParent.get("ROOT") || [] };
  }, [tree]);

  const renderNode = (u, depth = 0) => {
    const children = treeMap.get(u.user_id) || [];
    return (
      <div key={u.user_id} style={{ marginLeft: depth * 18 }} className="py-1">
        <div className="text-sm flex items-center gap-2">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" />
          <strong>{u.name}</strong>
          <span className="text-muted-foreground">({u.email})</span>
          <Badge variant="outline" className="text-[10px]">{u.role}</Badge>
          {u.is_blocked && <Badge variant="destructive" className="text-[10px]">{t("ad.tag_blocked")}</Badge>}
          {!u.is_approved && !u.is_blocked && <Badge variant="secondary" className="text-[10px]">{t("ad.tag_pending")}</Badge>}
          <span className="text-[10px] font-mono text-muted-foreground">{u.referral_code}</span>
        </div>
        {children.length > 0 && (
          <div className="border-l border-border/60 ml-1 pl-3">
            {children.map((c) => renderNode(c, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const stats = useMemo(() => {
    const referrerIds = new Set();
    tree.forEach((u) => { if (u.referrer_user_id) referrerIds.add(u.referrer_user_id); });
    const subtreeSize = (uid) => {
      let n = 0;
      const stack = [uid];
      while (stack.length) {
        const cur = stack.pop();
        const kids = treeMap.get(cur) || [];
        n += kids.length;
        kids.forEach((k) => stack.push(k.user_id));
      }
      return n;
    };
    const topReferrer = roots
      .map((r) => ({ user: r, size: subtreeSize(r.user_id) }))
      .sort((a, b) => b.size - a.size)[0];
    const noReferrer = tree.filter((u) => !u.referrer_user_id).length;
    return { totalUsers: tree.length, referrers: referrerIds.size, noReferrer, topReferrer };
  }, [tree, treeMap, roots]);

  const q = query.trim().toLowerCase();
  const matchesRecursive = (u) => {
    if (!q) return true;
    const hit = (u.name || "").toLowerCase().includes(q)
      || (u.email || "").toLowerCase().includes(q)
      || (u.referral_code || "").toLowerCase().includes(q);
    if (hit) return true;
    const kids = treeMap.get(u.user_id) || [];
    return kids.some(matchesRecursive);
  };
  const visibleRoots = q ? tree.filter((u) => !u.referrer_user_id && matchesRecursive(u)) : roots;

  return (
    <Card className="p-4 border-border/60">
      <h3 className="font-heading text-lg mb-2">{t("ad.referrals_heading")}</h3>
      <p className="text-xs text-muted-foreground mb-4">{t("ad.referrals_hint")}</p>

      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="admin-referrals-summary">
          <Card className="p-3 border-border/60">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Total users</div>
            <div className="font-heading text-2xl">{stats.totalUsers.toLocaleString()}</div>
          </Card>
          <Card className="p-3 border-border/60">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Active referrers</div>
            <div className="font-heading text-2xl">{stats.referrers.toLocaleString()}</div>
          </Card>
          <Card className="p-3 border-border/60">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">No referrer</div>
            <div className="font-heading text-2xl">{stats.noReferrer.toLocaleString()}</div>
          </Card>
          <Card className="p-3 border-border/60">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Top referrer subtree</div>
            <div className="font-heading text-sm truncate">
              {stats.topReferrer?.user?.name || "—"}
              <span className="text-primary font-mono ml-1">·{stats.topReferrer?.size ?? 0}</span>
            </div>
          </Card>
        </div>
        <Input
          placeholder="Search name / email / referral code…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="admin-referrals-search"
        />
        <div className="text-sm max-h-[60vh] overflow-auto rounded-lg border border-border/60 p-3 bg-muted/20" data-testid="admin-referrals-tree">
          {visibleRoots.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-6">No matches.</div>
          ) : (
            visibleRoots.map((u) => renderNode(u))
          )}
        </div>
      </div>
    </Card>
  );
}
